import { Head,  router, usePage } from '@inertiajs/react'
import { useEffect, useMemo, useState } from 'react'

import Form from './form'
import { CommunityType } from './types'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {Card} from "@/components/ui/card.tsx";
import {Building2, Edit, Eye, MapPin, MoreVertical, Plus, Search, Trash2} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu.tsx";
import {Badge} from "@/components/ui/badge.tsx";

interface IndexProps {
  communities: CommunityType[]
}

export default function Index({ communities }: IndexProps) {
  const { flash } = usePage()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCommunity, setEditingCommunity] =
    useState<CommunityType | null>(null)
  const [searchTerm, setSearchTerm] = useState(() => {
    if (typeof window === 'undefined') return ''
    return new URLSearchParams(window.location.search).get('q') ?? ''
  })
  const [statusFilter, setStatusFilter] = useState(() => {
    if (typeof window === 'undefined') return 'all'
    return new URLSearchParams(window.location.search).get('status') ?? 'all'
  })

  const emptyCommunity = useMemo(
    () => ({
      id: 0,
      name: '',
      location: '',
      mayor: '',
      phone: '',
      email: '',
      code: '',
      latitude: '',
      longitude: '',
      status: 0,
    }),
    []
  )

  const isEditing = Boolean(editingCommunity)
  const formCommunity = editingCommunity ?? emptyCommunity
  const formAction = isEditing
    ? `/communities/${formCommunity.id}`
    : '/communities'
  const formMethod = isEditing ? 'patch' : 'post'
  const formTitle = isEditing ? 'Edit community' : 'New community'
  const formSubmitText = isEditing ? 'Update Community' : 'Create Community'

  const openCreateDialog = () => {
    setEditingCommunity(null)
    setDialogOpen(true)
  }

  const openEditDialog = (community: CommunityType) => {
    setEditingCommunity(community)
    setDialogOpen(true)
  }

  const statusLabel = (status: number) =>
    status === 1 ? 'Inactive' : 'Active'

  useEffect(() => {
    if (flash?.notice) {
      setDialogOpen(false)
      setEditingCommunity(null)
    }
  }, [flash?.notice])

  useEffect(() => {
    const timeout = setTimeout(() => {
      router.get(
        '/communities',
        {
          q: searchTerm.trim() || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['communities'],
        }
      )
    }, 300)

    return () => clearTimeout(timeout)
  }, [searchTerm, statusFilter])

  return (
    <>
      <Head title="Communities" />
      <div className="flex flex-col gap-6 p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Les communes</h1>
            <p className="text-sm text-muted-foreground">Gestion des différentes communes</p>
          </div>
          <Button onClick={() => openCreateDialog()} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Community
          </Button>
        </div>


        {/*<div className="mt-6 grid gap-4 md:grid-cols-[1fr_240px]">*/}
        {/*  <div>*/}
        {/*    <Label htmlFor="community-search">Search</Label>*/}
        {/*    <Input*/}
        {/*      id="community-search"*/}
        {/*      name="community-search"*/}
        {/*      value={searchTerm}*/}
        {/*      placeholder="Search by name or location"*/}
        {/*      onChange={(event) => setSearchTerm(event.target.value)}*/}
        {/*    />*/}
        {/*  </div>*/}
        {/*  <div>*/}
        {/*    <Label htmlFor="community-status">Status</Label>*/}
        {/*    <Select value={statusFilter} onValueChange={setStatusFilter}>*/}
        {/*      <SelectTrigger id="community-status">*/}
        {/*        <SelectValue placeholder="All statuses" />*/}
        {/*      </SelectTrigger>*/}
        {/*      <SelectContent>*/}
        {/*        <SelectItem value="all">All</SelectItem>*/}
        {/*        <SelectItem value="active">Active</SelectItem>*/}
        {/*        <SelectItem value="inactive">Inactive</SelectItem>*/}
        {/*      </SelectContent>*/}
        {/*    </Select>*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/* Search and Filters */}
        <Card className="border-border bg-card p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                  placeholder="Search communities by name, region, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
              />
            </div>
          </div>
        </Card>


          {/* Communities Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {communities.map((community) => (
                  <Card key={community.id} className="border-border bg-card p-6">
                      <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                                  <Building2 className="h-6 w-6 text-primary" />
                              </div>
                              <div>
                                  <h3 className="font-semibold text-foreground">{community.name}</h3>
                                  <p className="text-sm text-muted-foreground">{community.id}</p>
                              </div>
                          </div>
                          <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                      <MoreVertical className="h-4 w-4" />
                                  </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                  <DropdownMenuItem>
                                      <Eye className="mr-2 h-4 w-4" />
                                      View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                      onClick={() => {
                                          openEditDialog(community)
                                          //setIsEditDialogOpen(true)
                                      }}
                                  >
                                      <Edit className="mr-2 h-4 w-4" />
                                      Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem className="text-destructive">
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                  </DropdownMenuItem>
                              </DropdownMenuContent>
                          </DropdownMenu>
                      </div>

                      <div className="mt-4 space-y-3">
                          <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Region</span>
                              <div className="flex items-center gap-1 text-foreground">
                                  <MapPin className="h-3 w-3" />
                                  {community.location}
                              </div>
                          </div>

                          <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Population</span>
                              <span className="font-medium text-foreground">0</span>
                          </div>

                          <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Taxpayers</span>
                              <span className="font-medium text-foreground">0</span>
                          </div>

                          <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Agents</span>
                              <span className="font-medium text-foreground">0</span>
                          </div>

                          <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Total Collected</span>
                              <span className="font-medium text-foreground">0K</span>
                          </div>

                          <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Collection Rate</span>
                {/*              <span*/}
                {/*                  className={`font-medium ${*/}
                {/*                      community.collectionRate >= 85*/}
                {/*                          ? "text-chart-2"*/}
                {/*                          : community.collectionRate >= 70*/}
                {/*                              ? "text-chart-4"*/}
                {/*                              : "text-destructive"*/}
                {/*                  }`}*/}
                {/*              >*/}
                {/*  {community.collectionRate}%*/}
                {/*</span>*/}
                          </div>

                          <div className="flex items-center justify-between pt-2">
                              <Badge
                                  variant={
                                      community.status === 0 ? "default" : community.status === 1? "destructive" : "outline"
                                  }
                              >
                                  {community.status}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                  {/*Since {new Date(community.created_at).toLocaleDateString()}*/}
                </span>
                          </div>
                      </div>
                  </Card>
              ))}
          </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl dark">
          <DialogHeader>
            <DialogTitle>{formTitle}</DialogTitle>
          </DialogHeader>
          <Form
            key={`${isEditing ? formCommunity.id : 'new'}`}
            community={formCommunity}
            action={formAction}
            method={formMethod}
            submitText={formSubmitText}
            onSuccess={() => {
              setDialogOpen(false)
              setEditingCommunity(null)
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
